import { useEffect, useState } from "react";
import { api } from "./api";

/**
 * Polls GET /api/lives/today every `intervalMs` ms and returns the most
 * relevant drop for *now* — the currently airing live if one exists, else the
 * next scheduled drop (today or later). Returns `null` when nothing is
 * scheduled or the endpoint errors, so callers can render a fallback (e.g. the
 * Drop #001 launch countdown).
 *
 * Shared between the Home NextDropCard (mode-switching hero) and SiteHeader
 * (persistent "LIVE NOW" strip) so both surfaces flip in lockstep.
 */
export function useTodayLive({ intervalMs = 15000 } = {}) {
  const [live, setLive] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      api.get("/lives/today")
        .then((r) => { if (!cancelled) { setLive(r.data || null); setLoaded(true); } })
        .catch(() => { if (!cancelled) { setLive(null); setLoaded(true); } });
    };
    load();
    const id = setInterval(load, intervalMs);
    return () => { cancelled = true; clearInterval(id); };
  }, [intervalMs]);

  return { live, loaded };
}

/**
 * Reduce the raw live doc to a single UX mode the Home hero + top strip use.
 *   - "live"     — drop is actively broadcasting (join immediately)
 *   - "final"    — 2-minute checkout window (still joinable, price locked at floor)
 *   - "preshow"  — host has opened the room but not started (≤10 min before start)
 *   - "preopen"  — scheduled today, host hasn't opened yet, but ≤5 min from start
 *                 (chat is unlocked; users can enter waiting room and chat)
 *   - "today"    — scheduled for today, waiting for host to open (>5 min out)
 *   - "upcoming" — scheduled for a future day (>today)
 *   - "idle"     — nothing scheduled (null response)
 */
export function liveMode(doc) {
  if (!doc) return "idle";
  const s = doc.status;
  if (s === "live") return "live";
  if (s === "final_window") return "final";
  if (s === "open") return "preshow";
  if (s === "scheduled") {
    const scheduled = new Date(doc.scheduled_at).getTime();
    const now = Date.now();
    // Within the 5-minute pre-open window? Chat is already unlocked server-side,
    // so surface a "preopen" mode so the UI can show an "Enter Drop" CTA.
    if (scheduled - now <= 5 * 60 * 1000 && scheduled - now > -30 * 60 * 1000) return "preopen";
    // Same IST calendar day → "today" (host will open shortly)
    const istOffsetMs = 5.5 * 60 * 60 * 1000;
    const schedIstDay = new Date(scheduled + istOffsetMs).toISOString().slice(0, 10);
    const nowIstDay = new Date(now + istOffsetMs).toISOString().slice(0, 10);
    return schedIstDay === nowIstDay ? "today" : "upcoming";
  }
  return "idle";
}
